/* eslint-disable */
import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import DataTable from "../DataTable/DataTable";
import { getUsersList, getStatisticsSuccess } from "../../../actions";
import { history } from "../../../config/stores";
import Header from "../../Header/Header";
import exportIcon from '../../../assets/icons/export_file_icon.svg';
import * as XLSX from 'xlsx';
import moment from 'moment';
import { toast } from 'react-toastify';
import "./UsersList.scss";

const UsersList = ({ getUsersList, getStatisticsSuccess, usersList, location }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [periodFilter, setPeriodFilter] = useState({
    startDate: "",
    endDate: "",
    periodLabel: "All time",
    periodType: "all",
    role: "",
    lang: ""
  });

  // Role mapping for user-friendly display names
  const roleDisplayNames = {
    "landlordWithTenant": "Landlords with Tenant",
    "landlordWithoutTenant": "Landlords without Tenant", 
    "tenant": "Tenants",
    "tenantWithoutLandlord": "Tenants without Landlord",
    "owner": "Owners",
    "communityManager": "Community Managers",
    "userWithoutProperty": "Users without Property"
  };

  // Language mapping for user-friendly display names
  const languageDisplayNames = {
    "bg": "Bulgarian",
    "en": "English",
    "de": "German", 
    "it": "Italian"
  };

  // Parse URL parameters for period filtering and role filtering
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const startDate = urlParams.get('startDate') || "";
    const endDate = urlParams.get('endDate') || "";
    const periodLabel = urlParams.get('periodLabel') || "All time";
    const periodType = urlParams.get('periodType') || "all";
    const role = urlParams.get('role') || "";
    const lang = urlParams.get('lang') || "";
    
    setPeriodFilter({
      startDate,
      endDate,
      periodLabel,
      periodType,
      role,
      lang
    });
  }, []);

  useEffect(() => {
    // Set loading state and fetch users list
    getStatisticsSuccess({ type: "usersList", loading: true });
    setCurrentPage(1); // Reset to page 1
    setIsLoading(true);
    
    const apiPayload = {
      name: "",
      email: "",
      country: "",
      city: "",
      os: "",
      langs: periodFilter.lang ? [periodFilter.lang] : [],
      subscriptionTypes: [],
      enabledNotifications: [],
      phoneNumber: "",
      role: periodFilter.role,
      startDate: periodFilter.startDate,
      endDate: periodFilter.endDate,
      page: 1,
      limit: 10,
      onSuccess: () => setIsLoading(false),
      onError: () => setIsLoading(false)
    };
    
    getUsersList(apiPayload);
  }, [periodFilter]); // Re-run when period filter changes

  const handleLoadMore = () => {
    console.log('handleLoadMore called, hasNextPage:', usersList?.hasNextPage, 'currentPage:', currentPage);
    if (usersList?.hasNextPage && !isLoading) {
      const nextPage = currentPage + 1;
      setCurrentPage(nextPage);
      setIsLoading(true);
      
      const apiPayload = {
        name: "",
        email: "",
        country: "",
        city: "",
        os: "",
        langs: periodFilter.lang ? [periodFilter.lang] : [],
        subscriptionTypes: [],
        enabledNotifications: [],
        phoneNumber: "",
        role: periodFilter.role,
        startDate: periodFilter.startDate,
        endDate: periodFilter.endDate,
        page: nextPage,
        limit: 10,
        onSuccess: () => {
          console.log('Load more success for page:', nextPage);
          setIsLoading(false);
        },
        onError: (error) => {
          console.log('Load more error for page:', nextPage, error);
          setIsLoading(false);
        }
      };
      
      getUsersList(apiPayload);
    }
  };

  const handleBackToDashboard = () => {
    history.push('/');
  };

  const handleExport = async () => {
    try {
      toast.info("Starting export...");
      
      // Determine if we need to load all data or use current filtered data
      const hasFilters = periodFilter.role || periodFilter.lang || periodFilter.startDate || periodFilter.endDate;
      
      let dataToExport = [];
      
      if (hasFilters) {
        // Export filtered data - load all pages with current filters
        const allFilteredUsers = await loadAllFilteredUsers();
        dataToExport = allFilteredUsers;
      } else {
        // Export all users without filters - load everything
        const allUsers = await loadAllUsers();
        dataToExport = allUsers;
      }

      if (!dataToExport || dataToExport.length === 0) {
        toast.warn("No data to export");
        return;
      }

      // Create workbook and worksheet
      const wb = XLSX.utils.book_new();
      
      // Define headers matching the table structure
      const headers = [
        'Name',
        'Country', 
        'City',
        'OS',
        'Language',
        'Subscriptions',
        'Notifications',
        'Location',
        'Phone number',
        'Email',
        'User since',
        'Added properties',
        'Managing communities'
      ];
      
      // Transform data rows
      const rows = dataToExport.map(user => {
        return [
          user.fullName || '---',
          user.country || '---',
          user.city || '---',
          user.os ? user.os.charAt(0).toUpperCase() + user.os.slice(1) : '---',
          user.lang ? user.lang.toUpperCase() : '---',
          user.subscriptionType ? user.subscriptionType.charAt(0).toUpperCase() + user.subscriptionType.slice(1) : '---',
          user.enabledNotifications ? 'Yes' : 'No',
          (user.country && user.city) ? 'Yes' : 'No',
          user.phoneNumber || '---',
          user.email || '---',
          user.createdAt ? new Date(user.createdAt).toLocaleDateString('en-GB').replace(/\//g, '.') : '---',
          user.properties ? user.properties.length : '0',
          user.communities ? user.communities.length : '0'
        ];
      });
      
      // Create worksheet
      XLSX.utils.book_append_sheet(
        wb,
        XLSX.utils.aoa_to_sheet([headers, ...rows]),
        "Users"
      );
      
      // Generate filename with current date and filter info
      let filename = `users-${moment().format("DD-MM-YYYY")}`;
      
      if (periodFilter.role || periodFilter.lang || (periodFilter.startDate && periodFilter.endDate)) {
        filename += '-filtered';
        if (periodFilter.role) {
          const roleDisplayName = roleDisplayNames[periodFilter.role] || periodFilter.role;
          filename += `-${roleDisplayName.replace(/\s+/g, '-').toLowerCase()}`;
        }
        if (periodFilter.lang) {
          const langDisplayName = languageDisplayNames[periodFilter.lang] || periodFilter.lang;
          filename += `-${langDisplayName.replace(/\s+/g, '-').toLowerCase()}`;
        }
        if (periodFilter.startDate && periodFilter.endDate) {
          filename += `-${moment(periodFilter.startDate).format("DD-MM")}-to-${moment(periodFilter.endDate).format("DD-MM")}`;
        }
      }
      
      // Write file
      XLSX.writeFile(wb, `${filename}.xlsx`);
      toast.success(`Exported ${dataToExport.length} users successfully!`);
      
    } catch (error) {
      console.error('Export error:', error);
      toast.error("Failed to export users data");
    }
  };

  const loadAllFilteredUsers = async () => {
    return new Promise((resolve, reject) => {
      const allUsers = [];
      let currentPage = 1;
      
      const loadPage = () => {
        const apiPayload = {
          name: "",
          email: "",
          country: "",
          city: "",
          os: "",
          langs: periodFilter.lang ? [periodFilter.lang] : [],
          subscriptionTypes: [],
          enabledNotifications: [],
          phoneNumber: "",
          role: periodFilter.role,
          startDate: periodFilter.startDate,
          endDate: periodFilter.endDate,
          page: currentPage,
          limit: 100, // Larger page size for export
          onSuccess: (response) => {
            const newUsers = response.docs || [];
            allUsers.push(...newUsers);
            
            if (response.hasNextPage) {
              currentPage++;
              loadPage();
            } else {
              resolve(allUsers);
            }
          },
          onError: (error) => {
            console.error('Error loading page:', currentPage, error);
            reject(error);
          }
        };
        
        getUsersList(apiPayload);
      };
      
      loadPage();
    });
  };

  const loadAllUsers = async () => {
    return new Promise((resolve, reject) => {
      const allUsers = [];
      let currentPage = 1;
      
      const loadPage = () => {
        const apiPayload = {
          name: "",
          email: "",
          country: "",
          city: "",
          os: "",
          langs: [],
          subscriptionTypes: [],
          enabledNotifications: [],
          phoneNumber: "",
          role: "",
          startDate: "",
          endDate: "",
          page: currentPage,
          limit: 100, // Larger page size for export
          onSuccess: (response) => {
            const newUsers = response.docs || [];
            allUsers.push(...newUsers);
            
            if (response.hasNextPage) {
              currentPage++;
              loadPage();
            } else {
              resolve(allUsers);
            }
          },
          onError: (error) => {
            console.error('Error loading page:', currentPage, error);
            reject(error);
          }
        };
        
        getUsersList(apiPayload);
      };
      
      loadPage();
    });
  };

  return (
    <>
      <Header />
      <div className="main-container">
        <div className="inner-header-container">
          <div className="left-part">
            <div 
              className="close-icon" 
              onClick={handleBackToDashboard}
              title="Back to Dashboard"
            >
              ×
            </div>
            <div className="inner-title">REGISTERED USERS</div>
          </div>
          <div className="right-part">
            {(periodFilter.role || periodFilter.lang) && (
              <div className="filter-info">
                <span className="filter-label">FILTERED BY:</span>
                <span className="filter-value">
                  {periodFilter.role && `ROLE - ${roleDisplayNames[periodFilter.role]?.toUpperCase() || periodFilter.role.toUpperCase()}`}
                  {periodFilter.role && periodFilter.lang && ' | '}
                  {periodFilter.lang && `LANGUAGE - ${languageDisplayNames[periodFilter.lang]?.toUpperCase() || periodFilter.lang.toUpperCase()}`}
                </span>
              </div>
            )}
            <div className="export-icon-container">
              <img 
                src={exportIcon} 
                alt="Export" 
                className="export-icon-styles"
                onClick={handleExport}
                style={{ cursor: "pointer" }}
              />
            </div>
          </div>
        </div>
        <div className="body-container">
          <DataTable
            data={{
              docs: usersList?.docs || [],
              totalPages: usersList?.totalPages || 0,
              page: usersList?.page || 1,
              totalDocs: usersList?.totalDocs || 0,
              hasNextPage: usersList?.hasNextPage || false
            }}
            preset="usersList"
            loading={usersList?.loading || isLoading}
            onLoadMore={handleLoadMore}
            emptyStateText="No users found"
          />
        </div>
      </div>
    </>
  );
};

const mapStateToProps = (state) => ({
  usersList: state.dashboard.usersList,
});

const mapDispatchToProps = (dispatch) => ({
  getUsersList: (payload) => dispatch(getUsersList(payload)),
  getStatisticsSuccess: (payload) => dispatch(getStatisticsSuccess(payload)),
});

export default connect(mapStateToProps, mapDispatchToProps)(UsersList);
